import { useState, useMemo, useCallback, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Search } from "lucide-react";

/**
 * Column configuration for DataTable
 */
export interface DataTableColumn<T> {
	/** Unique key for the column */
	key: string;
	/** Display label for the column header */
	label: string;
	/** Whether this column is sortable */
	sortable?: boolean;
	/** Custom render function for the cell */
	render?: (item: T) => ReactNode;
	/** Accessor function to get the value for sorting/filtering */
	// biome-ignore lint/suspicious/noExplicitAny: Generic accessor can return any type
	accessor?: (item: T) => any;
	/** CSS class for the cell */
	className?: string;
}

/**
 * Props for DataTable component
 */
export interface DataTableProps<T> {
	/** Array of data items to display */
	data: T[];
	/** Column configuration */
	columns: DataTableColumn<T>[];
	/** Optional title for the table card */
	title?: string;
	/** Optional description/subtitle */
	description?: string;
	/** Enable search functionality */
	searchable?: boolean;
	/** Placeholder text for search input */
	searchPlaceholder?: string;
	/** Custom search function (default searches all string fields) */
	searchFilter?: (item: T, searchTerm: string) => boolean;
	/** Items per page options */
	itemsPerPageOptions?: number[];
	/** Default items per page */
	defaultItemsPerPage?: number;
	/** Default sort field */
	defaultSortField?: string;
	/** Default sort order */
	defaultSortOrder?: "asc" | "desc";
	/** Show pagination controls */
	showPagination?: boolean;
	/** Custom empty state message */
	emptyMessage?: string;
	/** Additional actions to render in the header */
	headerActions?: ReactNode;
	/** Loading state */
	loading?: boolean;
	/** Custom loading component */
	loadingComponent?: ReactNode;
}

/**
 * Reusable DataTable component with sorting, filtering, and pagination
 *
 * @example
 * ```tsx
 * <DataTable
 *   data={users}
 *   columns={[
 *     { key: "email", label: "Email", sortable: true },
 *     { key: "balance", label: "Balance", sortable: true, render: (user) => `${user.balance} coins` }
 *   ]}
 *   searchable
 *   title="Users"
 * />
 * ```
 */
// biome-ignore lint/suspicious/noExplicitAny: Generic data type needs flexible indexing
export function DataTable<T extends Record<string, any>>({
	data,
	columns,
	title,
	description,
	searchable = false,
	searchPlaceholder = "Search...",
	searchFilter,
	itemsPerPageOptions = [10, 25, 50, 100],
	defaultItemsPerPage = 25,
	defaultSortField,
	defaultSortOrder = "desc",
	showPagination = true,
	emptyMessage = "No data available",
	headerActions,
	loading = false,
	loadingComponent,
}: DataTableProps<T>) {
	const [searchTerm, setSearchTerm] = useState("");
	const [sortField, setSortField] = useState<string | undefined>(
		defaultSortField,
	);
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">(defaultSortOrder);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

	// Default search filter function
	const defaultSearchFilter = useCallback((item: T, term: string): boolean => {
		const lowerTerm = term.toLowerCase();
		return Object.values(item).some((value) => {
			if (value === null || value === undefined) return false;
			return String(value).toLowerCase().includes(lowerTerm);
		});
	}, []);

	// Filter and sort data
	const filteredAndSortedData = useMemo(() => {
		let filtered = [...data];

		// Apply search filter
		if (searchable && searchTerm) {
			const filterFn = searchFilter || defaultSearchFilter;
			filtered = filtered.filter((item) => filterFn(item, searchTerm));
		}

		// Apply sorting
		if (sortField) {
			const column = columns.find((col) => col.key === sortField);
			filtered = filtered.sort((a, b) => {
				// biome-ignore lint/suspicious/noExplicitAny: Dynamic value comparison
				let aValue: any;
				// biome-ignore lint/suspicious/noExplicitAny: Dynamic value comparison
				let bValue: any;

				if (column?.accessor) {
					aValue = column.accessor(a);
					bValue = column.accessor(b);
				} else {
					aValue = a[sortField];
					bValue = b[sortField];
				}

				// Handle null/undefined values
				if (aValue === null || aValue === undefined) return 1;
				if (bValue === null || bValue === undefined) return -1;

				// Convert dates to timestamps for comparison
				if (aValue instanceof Date) aValue = aValue.getTime();
				if (bValue instanceof Date) bValue = bValue.getTime();
				if (typeof aValue === "string" && !Number.isNaN(Date.parse(aValue))) {
					const aDate = new Date(aValue).getTime();
					const bDate = new Date(bValue).getTime();
					if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
						aValue = aDate;
						bValue = bDate;
					}
				}

				if (sortOrder === "asc") {
					return aValue > bValue ? 1 : -1;
				}
				return aValue < bValue ? 1 : -1;
			});
		}

		return filtered;
	}, [
		data,
		searchTerm,
		sortField,
		sortOrder,
		columns,
		searchable,
		searchFilter,
		defaultSearchFilter,
	]);

	// Paginate data
	const paginatedData = useMemo(() => {
		if (!showPagination) return filteredAndSortedData;
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredAndSortedData.slice(startIndex, endIndex);
	}, [filteredAndSortedData, currentPage, itemsPerPage, showPagination]);

	const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);

	// Handle sort
	const handleSort = (field: string) => {
		const column = columns.find((col) => col.key === field);
		if (!column?.sortable) return;

		if (sortField === field) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortOrder("asc");
		}
		setCurrentPage(1); // Reset to first page when sorting changes
	};

	// Handle search change
	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setCurrentPage(1); // Reset to first page when searching
	};

	// Handle items per page change
	const handleItemsPerPageChange = (value: string) => {
		setItemsPerPage(Number.parseInt(value));
		setCurrentPage(1);
	};

	// Default loading component
	const defaultLoadingComponent = (
		<div className="flex items-center justify-center py-12">
			<div className="text-center">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto" />
				<p className="mt-4 text-gray-600">Loading...</p>
			</div>
		</div>
	);

	const content = loading ? (
		loadingComponent || defaultLoadingComponent
	) : (
		<>
			{/* Results info */}
			<div className="mb-4 text-sm text-gray-600">
				Showing {paginatedData.length} of {filteredAndSortedData.length}{" "}
				{filteredAndSortedData.length === 1 ? "item" : "items"}
				{searchTerm && " (filtered)"}
			</div>

			{/* Table */}
			<div className="rounded-md border overflow-x-auto">
				<Table>
					<TableHeader>
						<TableRow>
							{columns.map((column) => (
								<TableHead key={column.key} className={column.className}>
									{column.sortable ? (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleSort(column.key)}
											className="hover:bg-transparent"
										>
											{column.label}
											<ArrowUpDown className="ml-2 h-4 w-4" />
										</Button>
									) : (
										column.label
									)}
								</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{paginatedData.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={columns.length}
									className="text-center py-12 text-gray-500"
								>
									{emptyMessage}
								</TableCell>
							</TableRow>
						) : (
							paginatedData.map((item, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: Data may not have stable unique IDs
								<TableRow key={index}>
									{columns.map((column) => (
										<TableCell key={column.key} className={column.className}>
											{column.render
												? column.render(item)
												: column.accessor
													? column.accessor(item)
													: item[column.key]}
										</TableCell>
									))}
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			{/* Pagination */}
			{showPagination && totalPages > 1 && (
				<div className="flex items-center justify-between mt-4">
					<div className="text-sm text-gray-600">
						Seite {currentPage} / {totalPages}
					</div>
					<div className="flex gap-2 justify-center flex-1">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
							disabled={currentPage === 1}
						>
							Zurück
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
							disabled={currentPage === totalPages}
						>
							Weiter
						</Button>
					</div>
				</div>
			)}
		</>
	);

	// If title is provided, wrap in Card
	if (title || description || searchable || headerActions) {
		return (
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							{title && <CardTitle>{title}</CardTitle>}
							{description && (
								<p className="text-sm text-muted-foreground mt-1">
									{description}
								</p>
							)}
						</div>
						{headerActions && <div>{headerActions}</div>}
					</div>
					{searchable && (
						<div className="flex flex-col sm:flex-row gap-4 mt-4">
							{/* Search */}
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
								<Input
									placeholder={searchPlaceholder}
									value={searchTerm}
									onChange={(e) => handleSearchChange(e.target.value)}
									className="pl-10"
								/>
							</div>

							{/* Items per page */}
							{showPagination && (
								<Select
									value={itemsPerPage.toString()}
									onValueChange={handleItemsPerPageChange}
								>
									<SelectTrigger className="w-[140px]">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{itemsPerPageOptions.map((option) => (
											<SelectItem key={option} value={option.toString()}>
												{option} pro Seite
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							)}
						</div>
					)}
				</CardHeader>
				<CardContent>{content}</CardContent>
			</Card>
		);
	}

	// Return just the table without Card wrapper
	return <div>{content}</div>;
}
